import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  useSalesViewers,
  useInviteOrGrantSalesViewer,
  useRevokeSalesViewers,
} from '../../hooks/useExternalSalesViewers';
import {
  suggestExternalUsersForGrant,
  type SalesViewerSuggestion,
} from '../../services/api/externalSalesViewers';

export interface InviteExternalViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Callback invoked once the server returns success — caller renders a toast. */
  onSuccess?: (msg: string) => void;
  /** Callback invoked on server error — caller renders a toast. */
  onError?: (msg: string) => void;
}

// Tiny inline debounce — mirrors `InviteExternalModal`'s helper.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Tasks-parity grant surface for the internal Leads page (Phase 105, Plan
 * 105-07). Structural copy of `InviteExternalModal` (the kanban board's
 * "External users" modal) with the project dimension removed — D-01 makes
 * the sales-viewer grant global, not project-keyed.
 *
 * Two stacked sections:
 *   1. Viewers — read-side list of external users currently holding the
 *      grant; each row has a checkbox; "Revoke selected" removes the grant
 *      (does NOT delete the external user or touch their project
 *      memberships — that's a distinct concept from this grant, D-01).
 *   2. Grant access — email input with a server-backed typeahead over
 *      active external_users. Picking a suggestion fills email + names so
 *      the submit hits the existing-user branch (no invite email). A new
 *      email requires firstName + lastName, enforced here client-side (and
 *      again server-side).
 *
 * D-07: this modal itself carries no access-control logic — the "External
 * users" button that mounts it is gated on `isAdmin` in `LeadsPage.tsx`,
 * and every backend endpoint this modal calls is `requireAdmin`-gated
 * independently. The frontend gate is UX only.
 */
export function InviteExternalViewerModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: InviteExternalViewerModalProps) {
  // --- Viewers section -----------------------------------------------------
  const viewersQuery = useSalesViewers(isOpen);
  const revokeMutation = useRevokeSalesViewers();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Reset checkbox state when the visible viewer list changes (e.g. after a
  // successful revoke). Mirrors InviteExternalModal's member-list reset.
  const viewerIds = useMemo(
    () => (viewersQuery.data ?? []).map((v) => v.id).join(','),
    [viewersQuery.data],
  );
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(viewerIds === '' ? [] : viewerIds.split(',').map(Number));
      const next = new Set<number>();
      for (const id of prev) if (visible.has(id)) next.add(id);
      return next;
    });
  }, [viewerIds]);

  function toggleSelected(id: number, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleRevokeSelected() {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    revokeMutation.mutate(ids, {
      onSuccess: (data) => {
        const revokedCount = data.revoked.length;
        if (revokedCount === 0) {
          if (onSuccess) onSuccess('Nothing to revoke.');
          return;
        }
        if (onSuccess)
          onSuccess(
            revokedCount === 1
              ? '1 viewer had lead access revoked.'
              : `${revokedCount} viewers had lead access revoked.`,
          );
      },
      onError: (err) => {
        if (onError) onError(err.message ?? 'Server error');
      },
    });
  }

  // --- Grant access section -------------------------------------------------
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pickedSuggestion, setPickedSuggestion] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inviteMutation = useInviteOrGrantSalesViewer();

  // Reset on close so the modal re-opens with a clean slate.
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setPickedSuggestion(false);
      setValidationError(null);
      setSelectedIds(new Set());
      inviteMutation.reset();
      revokeMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const debouncedEmail = useDebounced(email.trim(), 200);
  const suggestQuery = useQuery<SalesViewerSuggestion[], Error>({
    queryKey: ['sales-viewer-suggest', debouncedEmail],
    queryFn: () => suggestExternalUsersForGrant(debouncedEmail),
    enabled: isOpen && debouncedEmail.length >= 2 && !pickedSuggestion,
    staleTime: 30_000,
  });
  const suggestions = suggestQuery.data ?? [];
  const showSuggestions =
    isOpen && !pickedSuggestion && debouncedEmail.length >= 2 && suggestions.length > 0;

  function pickSuggestion(s: SalesViewerSuggestion) {
    setEmail(s.email);
    setFirstName(s.firstName);
    setLastName(s.lastName);
    setPickedSuggestion(true);
    setValidationError(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    // Client-side mirror of the server's FIRST_AND_LAST_NAME_REQUIRED rule
    // for a brand-new email. We don't know server-side whether the email is
    // new, but a picked suggestion always carries names, so a bare email
    // with no names typed is only reachable for a genuinely new address.
    if (!pickedSuggestion && (trimmedFirst.length === 0 || trimmedLast.length === 0)) {
      setValidationError('First and last name are required for a new email address.');
      return;
    }
    setValidationError(null);

    inviteMutation.mutate(
      {
        email: trimmedEmail,
        firstName: trimmedFirst === '' ? undefined : trimmedFirst,
        lastName: trimmedLast === '' ? undefined : trimmedLast,
      },
      {
        onSuccess: (data) => {
          // Three-way action union, named explicitly (not a fallthrough
          // else) so 'already_granted' is a distinct, grep-able branch.
          let message: string;
          switch (data.action) {
            case 'invited':
              message = `Invite sent to ${trimmedEmail}.`;
              break;
            case 'granted':
              message = `Access granted to ${trimmedEmail}.`;
              break;
            case 'already_granted':
              message = `${trimmedEmail} already has access.`;
              break;
          }
          if (onSuccess) onSuccess(message);
          setEmail('');
          setFirstName('');
          setLastName('');
          setPickedSuggestion(false);
        },
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }

  const canSubmit = email.trim().length > 0 && !inviteMutation.isPending;
  const viewers = viewersQuery.data ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title="External users with lead access">
      <div className="space-y-5">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            A granted viewer sees <strong>all leads across every customer</strong>, including
            deal values, per-role rates, and internal sales notes. There is no per-project or
            per-customer scoping on this grant.
          </p>
        </div>

        {/* ===================== Viewers section ===================== */}
        <section className="space-y-2">
          <header className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Current viewers</h3>
            <Button
              type="button"
              variant="danger"
              onClick={handleRevokeSelected}
              disabled={selectedIds.size === 0 || revokeMutation.isPending}
              isLoading={revokeMutation.isPending}
            >
              <TrashIcon className="w-4 h-4 mr-1 inline" />
              Revoke selected ({selectedIds.size})
            </Button>
          </header>
          <p className="text-xs text-gray-500">
            Revoking removes lead access only — it does not delete the external user or affect
            their project memberships.
          </p>
          {viewersQuery.isLoading ? (
            <p className="text-sm text-gray-400 italic">Loading viewers…</p>
          ) : viewersQuery.isError ? (
            <p className="text-sm text-red-600">Couldn&rsquo;t load viewers.</p>
          ) : viewers.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No external users have lead access yet.</p>
          ) : (
            <ul className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-64 overflow-auto">
              {viewers.map((v) => {
                const fullName = [v.firstName, v.lastName].filter(Boolean).join(' ');
                const checked = selectedIds.has(v.id);
                return (
                  <li
                    key={v.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSelected(v.id, e.target.checked)}
                      aria-label={`Select ${v.email} for revoke`}
                      className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-gray-800 truncate">{v.email}</span>
                      {fullName && (
                        <span className="text-xs text-gray-500 truncate">{fullName}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ===================== Grant access section ===================== */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Grant access</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-xs text-gray-500">
              Existing external users get access directly, no email. New emails get an invite
              email — provide first and last name in that case.
            </p>
            <div className="relative">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                Email
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setPickedSuggestion(false);
                    setValidationError(null);
                  }}
                  placeholder="someone@example.com"
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="Email"
                  autoComplete="off"
                />
              </label>
              {showSuggestions && (
                <ul
                  role="listbox"
                  aria-label="Existing external users"
                  className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-md"
                >
                  {suggestions.map((s) => {
                    const fullName = [s.firstName, s.lastName].filter(Boolean).join(' ');
                    return (
                      <li key={s.id} role="option" aria-selected="false">
                        <button
                          type="button"
                          onClick={() => pickSuggestion(s)}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-primary-50"
                        >
                          <span className="font-medium text-gray-800">{s.email}</span>
                          {fullName && (
                            <span className="ml-2 text-xs text-gray-500">{fullName}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                First name (new users)
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="First name"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                Last name (new users)
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="Last name"
                />
              </label>
            </div>
            {validationError && <p className="text-xs text-red-600">{validationError}</p>}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
              <Button type="button" variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!canSubmit}
                isLoading={inviteMutation.isPending}
              >
                Grant access
              </Button>
            </div>
          </form>
        </section>
      </div>
    </Modal>
  );
}
